import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Flag, ChevronRight, ChevronLeft, TrendingUp, TrendingDown, Trophy } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { BottomSheet, Empty, DeleteButton, Field, COLORI_SQUADRA, BulkCheck, BulkBar, ConfirmAction, fmtData } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const fmtPunti = (n) => `${n > 0 ? "+" : ""}${n}`;

const STEP_LABELS = ["1. Animati", "2. Animatori", "3. Responsabili", "4. Nome e riepilogo"];

function CheckList({ items, selected, onToggle, render, testPrefix, disabledIds = new Set() }) {
  const [q, setQ] = useState("");
  // Gli Animati e gli Animatori già presenti in un'altra squadra non devono
  // comparire proprio nel selettore. In modifica lasciamo comunque visibili
  // quelli già assegnati alla squadra corrente (disabledIds esclude già la
  // squadra in modifica).
  const filtered = items
    .filter((i) => `${i.nome} ${i.cognome}`.toLowerCase().includes(q.toLowerCase()))
    .filter((i) => !disabledIds.has(i.id) || selected.includes(i.id));
  return (
    <div>
      <input type="search" className="input-app mb-3" placeholder="Cerca..." value={q} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setQ(e.target.value)} data-testid={`${testPrefix}-search`} />
      <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
        {filtered.map((i) => (
          <button
            key={i.id}
            type="button"
            onClick={() => onToggle(i.id)}
            className={`w-full flex items-center justify-between rounded-2xl px-4 py-2.5 text-sm font-semibold border transition-colors ${
              selected.includes(i.id) ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
            data-testid={`${testPrefix}-item-${i.id}`}
          >
            <span>{i.nome} {i.cognome}</span>
            {render && render(i)}
          </button>
        ))}
        {filtered.length === 0 && <p className="text-xs text-slate-400 font-semibold text-center py-4">Nessun risultato</p>}
      </div>
    </div>
  );
}

export default function Squadre() {
  const [squadre, setSquadre] = useState([]);
  const [animati, setAnimati] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ nome: "", colore: COLORI_SQUADRA[0], animati_ids: [], animatori_ids: [], responsabili_ids: [] });
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [puoiGestire, setPuoiGestire] = useState(false);
  const [aggiust, setAggiust] = useState(null);   // sheet bonus/malus { squadra, segno }
  const [dettaglio, setDettaglio] = useState(null); // sheet dettaglio punti
  const [importo, setImporto] = useState("");
  const [motivo, setMotivo] = useState("");

  const load = useCallback(() => {
    api.get("/squadre").then((r) => setSquadre(r.data));
    api.get("/animati").then((r) => setAnimati(r.data));
    api.get("/utenti").then((r) => setUtenti(r.data));
    api.get("/classifica/stato").then((r) => setPuoiGestire(!!r.data.puoi_gestire_punti)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  // Apre il dettaglio punti riga per riga della squadra
  const apriDettaglio = async (s) => {
    setDettaglio({ squadra: s, dati: null });
    try {
      const { data } = await api.get(`/squadre/${s.id}/dettaglio-punti`);
      setDettaglio({ squadra: s, dati: data });
    } catch (err) { toast.error(errMsg(err)); setDettaglio(null); }
  };

  const salvaAggiustamento = async (e) => {
    e.preventDefault();
    const n = Math.abs(parseInt(importo, 10));
    if (!n || isNaN(n)) return toast.error("Indica quanti punti");
    const punti = aggiust.segno === "bonus" ? n : -n;
    try {
      await api.post(`/squadre/${aggiust.squadra.id}/aggiustamento`, { punti, motivo: motivo || null });
      toast.success(`${aggiust.segno === "bonus" ? "Bonus" : "Malus"} registrato`);
      setAggiust(null); setImporto(""); setMotivo("");
      load();
      if (dettaglio?.squadra?.id === aggiust.squadra.id) apriDettaglio(aggiust.squadra);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const eliminaAggiustamento = async (aid, squadra) => {
    try {
      await api.delete(`/aggiustamenti/${aid}`);
      toast.success("Voce rimossa");
      load();
      apriDettaglio(squadra);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const staff = utenti.filter((u) => u.role === "animatore" || u.role === "aiuto_animatore");
  const responsabili = utenti.filter((u) => u.role === "responsabile");

  const occupatiAnimati = new Set(squadre.filter((s) => s.id !== editId).flatMap((s) => s.animati_ids || []));
  const occupatiAnimatori = new Set(squadre.filter((s) => s.id !== editId).flatMap((s) => s.animatori_ids || []));

  const toggle = (k) => (id) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] }));

  const apriNuova = () => {
    setEditId(null);
    setForm({ nome: "", colore: COLORI_SQUADRA[squadre.length % COLORI_SQUADRA.length], animati_ids: [], animatori_ids: [], responsabili_ids: [] });
    setStep(0);
    setWizardOpen(true);
  };

  const apriModifica = (s) => {
    setEditId(s.id);
    setForm({ nome: s.nome, colore: s.colore || COLORI_SQUADRA[0], animati_ids: s.animati_ids || [], animatori_ids: s.animatori_ids || [], responsabili_ids: s.responsabili_ids || [] });
    setStep(0);
    setWizardOpen(true);
  };

  const salva = async () => {
    setSaving(true);
    try {
      if (editId) await api.put(`/squadre/${editId}`, form);
      else await api.post("/squadre", form);
      toast.success(editId ? "Squadra aggiornata" : "Squadra creata!");
      setWizardOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async (id) => {
    try {
      await api.delete(`/squadre/${id}`);
      toast.success("Squadra eliminata");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const hasAnimatore = staff.some((u) => u.role === "animatore" && form.animatori_ids.includes(u.id));
  const canNext = [
    form.animati_ids.length >= 1,
    form.animatori_ids.length >= 1 && hasAnimatore,
    form.responsabili_ids.length >= 1,
    form.nome.trim().length >= 1,
  ][step];

  return (
    <div data-testid="squadre-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Squadre</h1>
          <p className="text-sm text-slate-500 font-semibold">{squadre.length} squadre attive</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {squadre.length > 0 && (
            <button className={bulkMode ? "btn-secondary" : "btn-ghost"} onClick={() => { if (bulkMode) { setBulkMode(false); setSelected([]); } else setBulkMode(true); }} data-testid="squadre-bulk-toggle">
              {bulkMode ? "Annulla selezione" : "Seleziona"}
            </button>
          )}
          <button className="btn-primary" onClick={apriNuova} data-testid="nuova-squadra-btn">
            <Plus className="h-4 w-4" /> Nuova squadra
          </button>
        </div>
      </div>

      {bulkMode && (
        <BulkBar count={selected.length} total={squadre.length} testId="squadre-bulk-bar"
          onSelectAll={() => setSelected(selected.length === squadre.length ? [] : squadre.map((s) => s.id))}
          onClear={() => { setBulkMode(false); setSelected([]); }}
          actions={
            <ConfirmAction onConfirm={async () => {
              try {
                const { data } = await api.post("/squadre/bulk/delete", { ids: selected });
                toast.success(`${data.deleted} squadre eliminate`);
                setBulkMode(false); setSelected([]); load();
              } catch (err) { toast.error(errMsg(err)); }
            }} title={`Eliminare ${selected.length} squadre?`} description="Gli Animati assegnati verranno lasciati senza squadra." testId="squadre-bulk-delete-confirm">
              <button type="button" disabled={selected.length === 0} className="btn-danger disabled:opacity-40 !py-2" data-testid="squadre-bulk-delete-btn">
                Elimina {selected.length > 0 && `(${selected.length})`}
              </button>
            </ConfirmAction>
          } />
      )}

      {squadre.length === 0 ? (
        <Empty icon={Flag} text="Nessuna squadra" sub="Crea la prima squadra con il flusso guidato" />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {squadre.map((s, i) => {
            const isSel = selected.includes(s.id);
            const toggle = () => setSelected((sel) => sel.includes(s.id) ? sel.filter((x) => x !== s.id) : [...sel, s.id]);
            return (
              <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.3) }}
                onClick={() => (bulkMode ? toggle() : apriDettaglio(s))}
                className={`card-app p-5 cursor-pointer hover:shadow-lg transition-shadow ${bulkMode && isSel ? "ring-2 ring-amber-400" : ""}`}
                data-testid={`squadra-card-${s.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {bulkMode && <BulkCheck checked={isSel} onClick={toggle} testId={`squadra-check-${s.id}`} />}
                  <span className="h-5 w-5 rounded-full shrink-0 border-2 border-white shadow" style={{ background: s.colore || "#94a3b8" }} />
                  <h2 className="font-display font-extrabold text-slate-900 text-lg flex-1 truncate">{s.nome}</h2>
                  {!!s.aggiustamento_totale && (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${s.aggiustamento_totale > 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`} data-testid={`squadra-agg-${s.id}`}>
                      {s.aggiustamento_totale > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {fmtPunti(s.aggiustamento_totale)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 font-semibold space-y-1 mb-4">
                  <p>Responsabili: {s.responsabili.map((r) => `${r.nome} ${r.cognome}`).join(", ") || "—"}</p>
                  <p>Animatori: {s.animatori.map((r) => `${r.nome} ${r.cognome}`).join(", ") || "—"}</p>
                  <p>{s.animati.length} Animati</p>
                </div>
                {!bulkMode && (
                  <div className="flex gap-2 justify-end flex-wrap">
                    {puoiGestire && (
                      <>
                        <button className="btn-ghost !py-2 text-xs !text-emerald-600" onClick={(e) => { e.stopPropagation(); setAggiust({ squadra: s, segno: "bonus" }); setImporto(""); setMotivo(""); }} data-testid={`bonus-${s.id}`}>
                          <TrendingUp className="h-3.5 w-3.5" /> Bonus
                        </button>
                        <button className="btn-ghost !py-2 text-xs !text-red-500" onClick={(e) => { e.stopPropagation(); setAggiust({ squadra: s, segno: "malus" }); setImporto(""); setMotivo(""); }} data-testid={`malus-${s.id}`}>
                          <TrendingDown className="h-3.5 w-3.5" /> Malus
                        </button>
                      </>
                    )}
                    <button className="btn-ghost !py-2 text-xs" onClick={(e) => { e.stopPropagation(); apriModifica(s); }} data-testid={`modifica-squadra-${s.id}`}>Modifica</button>
                    <span onClick={(e) => e.stopPropagation()}>
                      <DeleteButton onConfirm={() => elimina(s.id)} entity={`la squadra ${s.nome}`} label="" testId={`elimina-squadra-${s.id}`} />
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      <BottomSheet open={wizardOpen} onOpenChange={setWizardOpen} title={editId ? "Modifica squadra" : "Nuova squadra"} description={STEP_LABELS[step]} testId="squadra-wizard-sheet">
        <div className="flex gap-1.5 mb-5">
          {STEP_LABELS.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-amber-500" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 0 && (
          <>
            <p className="text-xs text-slate-400 font-semibold mb-3">Gli Animati già assegnati a un’altra squadra non vengono mostrati.</p>
            <CheckList items={animati} selected={form.animati_ids} onToggle={toggle("animati_ids")} disabledIds={occupatiAnimati} testPrefix="wizard-animati" />
          </>
        )}

        {step === 1 && (
          <>
            <p className="text-xs text-slate-400 font-semibold mb-3">Gli Animatori già assegnati a un’altra squadra non vengono mostrati. I Responsabili possono seguire più squadre.</p>
            {!hasAnimatore && form.animatori_ids.length > 0 && (
              <p className="badge-neg block w-full text-center py-2 mb-3 !text-xs">Serve almeno un Animatore (gli Aiuto Animatori da soli non bastano)</p>
            )}
            <CheckList items={staff} selected={form.animatori_ids} onToggle={toggle("animatori_ids")} disabledIds={occupatiAnimatori} testPrefix="wizard-staff"
              render={(u) => <span className={u.role === "animatore" ? "badge-warn" : "badge-pos"}>{u.role === "animatore" ? "Animatore" : "Aiuto"}</span>} />
          </>
        )}

        {step === 2 && (
          <CheckList items={responsabili} selected={form.responsabili_ids} onToggle={toggle("responsabili_ids")} testPrefix="wizard-resp" />
        )}

        {step === 3 && (
          <>
            <Field label="Nome della squadra">
              <input className="input-app" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="es. Delfini" data-testid="squadra-nome-input" />
            </Field>
            <Field label="Colore">
              <div className="flex gap-2 flex-wrap">
                {COLORI_SQUADRA.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, colore: c })}
                    className={`h-9 w-9 rounded-full border-4 transition-transform active:scale-90 ${form.colore === c ? "border-slate-800 scale-110" : "border-white"}`}
                    style={{ background: c }} data-testid={`colore-${c.slice(1)}`} />
                ))}
              </div>
            </Field>
            <div className="rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-600 space-y-1">
              <p>{form.animati_ids.length} Animati</p>
              <p>{form.animatori_ids.length} Animatori/Aiuti · {form.responsabili_ids.length} Responsabili</p>
            </div>
          </>
        )}

        <div className="flex justify-between mt-5">
          <button type="button" className="btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-indietro-btn">
            <ChevronLeft className="h-4 w-4" /> Indietro
          </button>
          {step < 3 ? (
            <button type="button" className="btn-primary" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="wizard-avanti-btn">
              Avanti <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" className="btn-primary" disabled={!canNext || saving} onClick={salva} data-testid="wizard-salva-btn">
              {saving ? "Salvataggio..." : "Salva squadra"}
            </button>
          )}
        </div>
      </BottomSheet>

      {/* SHEET BONUS / MALUS */}
      <BottomSheet open={!!aggiust} onOpenChange={(v) => { if (!v) setAggiust(null); }}
        title={aggiust ? `${aggiust.segno === "bonus" ? "Bonus" : "Malus"} · ${aggiust.squadra.nome}` : ""}
        description="I punti si sommano (o sottraggono) alla classifica generale." testId="aggiustamento-sheet">
        {aggiust && (
          <form onSubmit={salvaAggiustamento}>
            <Field label="Quanti punti">
              <input type="number" min="1" step="1" required className="input-app" value={importo}
                onChange={(e) => setImporto(e.target.value)} placeholder="Es. 5" autoFocus data-testid="aggiustamento-punti-input" />
            </Field>
            <Field label="Motivo (facoltativo)">
              <input className="input-app" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder={aggiust.segno === "bonus" ? "Es. fair play, ordine..." : "Es. ritardo, disordine..."} data-testid="aggiustamento-motivo-input" />
            </Field>
            <div className={`rounded-2xl p-3 mb-4 text-sm font-bold text-center ${aggiust.segno === "bonus" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
              {aggiust.segno === "bonus" ? "+" : "−"}{Math.abs(parseInt(importo, 10)) || 0} punti a {aggiust.squadra.nome}
            </div>
            <button className={`w-full ${aggiust.segno === "bonus" ? "btn-primary" : "btn-danger"}`} data-testid="aggiustamento-submit-btn">
              Conferma {aggiust.segno === "bonus" ? "bonus" : "malus"}
            </button>
          </form>
        )}
      </BottomSheet>

      {/* SHEET DETTAGLIO PUNTI (riga per riga) */}
      <BottomSheet open={!!dettaglio} onOpenChange={(v) => { if (!v) setDettaglio(null); }}
        title={dettaglio ? `Punti · ${dettaglio.squadra.nome}` : ""} testId="dettaglio-punti-sheet">
        {dettaglio && (!dettaglio.dati ? (
          <p className="text-slate-400 font-semibold text-center py-6">Caricamento...</p>
        ) : (
          <div>
            <div className="rounded-2xl bg-slate-900 text-white p-4 mb-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">Totale in classifica</p>
                <p className="text-xs text-slate-400 font-semibold">
                  {dettaglio.dati.totale_giochi} dai giochi {dettaglio.dati.totale_aggiustamenti ? `· ${fmtPunti(dettaglio.dati.totale_aggiustamenti)} bonus/malus` : ""}
                </p>
              </div>
              <p className="font-display text-2xl font-extrabold">{dettaglio.dati.totale} <span className="text-sm text-slate-400">pt</span></p>
            </div>

            {/* Giochi */}
            <p className="label-app">Giochi ({dettaglio.dati.righe_giochi.length})</p>
            {dettaglio.dati.righe_giochi.length === 0 ? (
              <p className="text-xs text-slate-400 font-semibold mb-4">Nessun gioco completato.</p>
            ) : (
              <div className="card-app divide-y divide-slate-50 mb-4">
                {dettaglio.dati.righe_giochi.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5" data-testid={`riga-gioco-${i}`}>
                    <span className="font-display font-extrabold text-slate-300 w-7 text-center text-sm">{r.posizione}°</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-700 truncate">{r.gioco_nome}</p>
                      <p className="text-[11px] text-slate-400 font-semibold truncate">{fmtData(r.data)} · {r.postazione_nome}</p>
                    </div>
                    <span className="font-display font-extrabold text-slate-900 shrink-0">+{r.punti}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Bonus / Malus */}
            {dettaglio.dati.aggiustamenti.length > 0 && (
              <>
                <p className="label-app">Bonus e Malus</p>
                <div className="card-app divide-y divide-slate-50">
                  {dettaglio.dati.aggiustamenti.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`riga-agg-${a.id}`}>
                      {a.punti > 0 ? <TrendingUp className="h-4 w-4 text-emerald-500 shrink-0" /> : <TrendingDown className="h-4 w-4 text-red-500 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-700 truncate">{a.motivo || (a.punti > 0 ? "Bonus" : "Malus")}</p>
                        <p className="text-[11px] text-slate-400 font-semibold truncate">{a.autore_nome} · {fmtData((a.created_at || "").slice(0, 10))}</p>
                      </div>
                      <span className={`font-display font-extrabold shrink-0 ${a.punti > 0 ? "text-emerald-600" : "text-red-500"}`}>{fmtPunti(a.punti)}</span>
                      {dettaglio.dati.puoi_gestire && (
                        <button type="button" onClick={() => eliminaAggiustamento(a.id, dettaglio.squadra)} className="text-slate-300 hover:text-red-500 shrink-0" aria-label="Rimuovi" data-testid={`rimuovi-agg-${a.id}`}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {dettaglio.dati.puoi_gestire && (
              <div className="flex gap-2 mt-4">
                <button className="btn-ghost !py-2 text-xs !text-emerald-600 flex-1" onClick={() => { const sq = dettaglio.squadra; setAggiust({ squadra: sq, segno: "bonus" }); setImporto(""); setMotivo(""); }} data-testid="dettaglio-bonus-btn">
                  <TrendingUp className="h-3.5 w-3.5" /> Bonus
                </button>
                <button className="btn-ghost !py-2 text-xs !text-red-500 flex-1" onClick={() => { const sq = dettaglio.squadra; setAggiust({ squadra: sq, segno: "malus" }); setImporto(""); setMotivo(""); }} data-testid="dettaglio-malus-btn">
                  <TrendingDown className="h-3.5 w-3.5" /> Malus
                </button>
              </div>
            )}
          </div>
        ))}
      </BottomSheet>
    </div>
  );
}
